export const NEGOTIATION_QUESTION_TYPES = [
  'text',
  'number',
  'link',
  'date',
  'list',
  'checkbox',
];

const NUMBER_FORMAT = /^-?\d+([.,]\d+)?$/;
const DATE_FORMAT = /^\d{4}-\d{2}-\d{2}$/;

export const negotiationQuestionTypeLabelKey = type =>
  `OPPORTUNITY_PIPELINES.NEGOTIATION_FORM.TYPES.${type}`;

export const hasActiveNegotiationForm = pipeline => {
  const negotiationForm = pipeline && pipeline.negotiation_form;
  return !!(
    negotiationForm &&
    negotiationForm.status === 'active' &&
    negotiationForm.questions_count > 0
  );
};

export const isQuestionRequiredAt = (question, stage) => {
  const requiredStage = question.required_pipeline_stage;
  if (!requiredStage || !stage || stage.position === undefined) {
    return false;
  }

  return requiredStage.position <= stage.position;
};

const isValidLink = text => {
  if (/[^\x21-\x7e]/.test(text)) {
    return false;
  }

  try {
    const { protocol, hostname } = new URL(text);
    return ['http:', 'https:'].includes(protocol) && hostname !== '';
  } catch (error) {
    return false;
  }
};

const isBlank = value =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '');

export const isQuestionAnswered = (question, answers = {}) => {
  const value = answers[String(question.id)];
  if (question.question_type === 'checkbox') {
    return value === true;
  }

  return !isBlank(value);
};

export const pendingRequiredQuestions = (questions = [], stage, answers) =>
  questions.filter(
    question =>
      isQuestionRequiredAt(question, stage) &&
      !isQuestionAnswered(question, answers)
  );

export const isAnswerValid = (question, value) => {
  if (isBlank(value)) {
    return true;
  }

  const text = String(value).trim();
  switch (question.question_type) {
    case 'number':
      return NUMBER_FORMAT.test(text);
    case 'link':
      return isValidLink(text);
    case 'date':
      return DATE_FORMAT.test(text);
    case 'list':
      return (question.list_values || []).includes(text);
    default:
      return true;
  }
};

export const invalidAnswerQuestions = (questions = [], answers = {}) =>
  questions.filter(
    question => !isAnswerValid(question, answers[String(question.id)])
  );

export const buildAnswersForm = (questions = [], answers = {}) =>
  questions.reduce((form, question) => {
    const value = answers[String(question.id)];
    if (question.question_type === 'checkbox') {
      form[question.id] = value === true;
    } else if (
      question.question_type === 'list' &&
      !(question.list_values || []).includes(value)
    ) {
      form[question.id] = '';
    } else {
      form[question.id] = isBlank(value) ? '' : String(value);
    }
    return form;
  }, {});

export const buildAnswersPayload = (questions = [], form = {}) =>
  questions.reduce((payload, question) => {
    const value = form[question.id];
    if (question.question_type === 'checkbox') {
      payload[question.id] = value === true ? true : null;
    } else {
      payload[question.id] = isBlank(value) ? null : String(value).trim();
    }
    return payload;
  }, {});

export const buildChangedAnswersPayload = (
  questions = [],
  form = {},
  savedForm = {}
) => {
  const answers = buildAnswersPayload(questions, form);
  const savedAnswers = buildAnswersPayload(questions, savedForm);
  return Object.keys(answers).reduce((changed, questionId) => {
    if (answers[questionId] !== savedAnswers[questionId]) {
      changed[questionId] = answers[questionId];
    }
    return changed;
  }, {});
};

export const pendingQuestionIdsFromError = error =>
  (error?.response?.data?.pending_negotiation_form_questions || []).map(
    question => question.id
  );
